(function () {
  if (customElements.get('upsell-bundles')) return;

  class UpsellBundles extends HTMLElement {
    constructor() {
      super();
      this.tiers = Array.from(this.querySelectorAll('[data-tier]'));
      this.tiers.forEach((tier) => {
        var header = tier.querySelector('[data-tier-header]');
        if (header) header.addEventListener('click', () => this.selectTier(tier, true));
        tier.querySelectorAll('[data-addon-checkbox]').forEach((checkbox) => {
          checkbox.addEventListener('click', (e) => e.stopPropagation());
          checkbox.addEventListener('change', () => this.updateTierPrice(tier));
        });
      });
      this.moneyFormat = window.Shopify && window.Shopify.money_format ? window.Shopify.money_format : '${{amount}}';
      this.showCurrencyCode = this.dataset.showCurrencyCode === 'true';
      this.currencyCode = this.dataset.currencyCode || '';
      this.form = null;
      this.submitBtn = null;
      this.busy = false;

      // Fixed-amount discounts are entered in the shop's base currency, but
      // Liquid renders tier/add-on prices already localized to the visitor's
      // currency (e.g. MXN) — subtracting the raw discount number straight
      // from that localized price under-discounts for every currency but the
      // base one. Recompute every tier/add-on discount here with the live
      // conversion rate before anything is shown, so the price on the page
      // matches what actually gets charged at checkout.
      this.recalculateForCurrency();

      this.connectToForm();
      this.initVariantPickers();

      var initiallySelected = this.tiers.find((t) => t.dataset.selected === 'true');
      if (initiallySelected) this.selectTier(initiallySelected, true);
    }

    // Tiers/add-ons whose linked product has more than one variant get a set
    // of native <select> option pickers (rendered in the Liquid snippets).
    // Wire them up here: on change, find the matching variant from the JSON
    // baked into the element, recompute the (possibly discounted) price the
    // same way the Liquid templates do, and refresh what's shown/added to cart.
    initVariantPickers() {
      this.tiers.forEach((tier) => {
        var tierVariants = this.parseVariantsJson(tier.dataset.variantsJson);
        if (tierVariants) {
          var unitEls = Array.from(tier.querySelectorAll('[data-tier-unit]'));
          if (unitEls.length) {
            // Multi-unit tier (quantity > 1): each unit has its own picker and
            // can end up with a different variant (e.g. 1 Black + 1 White).
            unitEls.forEach((unitEl) => {
              unitEl.querySelectorAll('select').forEach((select) => {
                select.addEventListener('click', (e) => e.stopPropagation());
                select.addEventListener('change', (e) => {
                  e.stopPropagation();
                  this.onTierUnitVariantChange(tier, unitEl, tierVariants);
                });
              });
            });
          } else {
            tier.querySelectorAll('[data-tier-variants] select').forEach((select) => {
              select.addEventListener('click', (e) => e.stopPropagation());
              select.addEventListener('change', (e) => {
                e.stopPropagation();
                this.onTierVariantChange(tier, tierVariants);
              });
            });
          }
        }

        tier.querySelectorAll('[data-addon]').forEach((addon) => {
          var addonVariants = this.parseVariantsJson(addon.dataset.variantsJson);
          if (!addonVariants) return;
          addon.querySelectorAll('[data-addon-variants] select').forEach((select) => {
            select.addEventListener('click', (e) => e.stopPropagation());
            select.addEventListener('change', (e) => {
              e.stopPropagation();
              this.onAddonVariantChange(addon, addonVariants);
            });
          });
        });
      });
    }

    parseVariantsJson(raw) {
      if (!raw) return null;
      try {
        var parsed = JSON.parse(raw);
        return Array.isArray(parsed) && parsed.length ? parsed : null;
      } catch (e) {
        return null;
      }
    }

    getSelectedOptionValues(container) {
      var selects = Array.from(container.querySelectorAll('select[data-option-position]'));
      selects.sort((a, b) => parseInt(a.dataset.optionPosition, 10) - parseInt(b.dataset.optionPosition, 10));
      return selects.map((select) => select.value);
    }

    findMatchingVariant(variants, values) {
      return variants.find((variant) => {
        for (var i = 0; i < values.length; i++) {
          var optionValue = variant['option' + (i + 1)] || null;
          if (optionValue !== values[i]) return false;
        }
        return true;
      });
    }

    // Live conversion rate from the shop's base currency to whatever the
    // visitor is browsing in. Populated by Shopify Markets when multi-currency
    // is active; defaults to 1 (no conversion) otherwise.
    getCurrencyRate() {
      if (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) {
        return parseFloat(window.Shopify.currency.rate) || 1;
      }
      return 1;
    }

    // Re-applies each tier's and add-on's percent/fixed discount using the
    // raw (pre-discount) localized price Liquid baked into data-raw-base-*,
    // so fixed-amount discounts get the same currency conversion the rest of
    // the price already has. Runs once at load, before the user sees anything.
    recalculateForCurrency() {
      var rate = this.getCurrencyRate();
      this.tiers.forEach((tier) => {
        var rawBase = parseInt(tier.dataset.rawBasePrice, 10);
        if (isNaN(rawBase)) return;
        var rawCompare = parseInt(tier.dataset.rawBaseComparePrice, 10);
        if (isNaN(rawCompare)) rawCompare = rawBase;
        var discountType = tier.dataset.discountType || 'none';
        var discountValue = parseFloat(tier.dataset.discountValue) || 0;
        var discounted = this.applyDiscount(rawBase, rawCompare, discountType, discountValue, rate);

        tier.dataset.basePrice = discounted.price;
        tier.dataset.baseComparePrice = discounted.compare;

        var priceEl = tier.querySelector('[data-tier-price]');
        if (priceEl) priceEl.textContent = this.formatMoney(discounted.price);
        var compareEl = tier.querySelector('[data-tier-compare-price]');
        if (compareEl) {
          compareEl.textContent = discounted.compare > discounted.price ? this.formatMoney(discounted.compare) : '';
        }

        tier.querySelectorAll('[data-addon]').forEach((addon) => {
          var addonRawBase = parseInt(addon.dataset.rawBasePrice, 10);
          if (isNaN(addonRawBase)) return;
          var addonRawCompare = parseInt(addon.dataset.rawBaseComparePrice, 10);
          if (isNaN(addonRawCompare)) addonRawCompare = addonRawBase;
          var addonDiscountType = addon.dataset.discountType || 'none';
          var addonDiscountValue = parseFloat(addon.dataset.discountValue) || 0;
          var addonDiscounted = this.applyDiscount(addonRawBase, addonRawCompare, addonDiscountType, addonDiscountValue, rate);

          addon.dataset.price = addonDiscounted.price;

          var addonPriceEl = addon.querySelector('.upsell-addon__price');
          if (addonPriceEl) addonPriceEl.textContent = this.formatMoney(addonDiscounted.price);
          var addonCompareEl = addon.querySelector('.upsell-addon__compare-price');
          if (addonCompareEl) {
            addonCompareEl.textContent =
              addonDiscounted.compare > addonDiscounted.price ? this.formatMoney(addonDiscounted.compare) : '';
          }
        });

        tier.querySelectorAll('[data-gift]').forEach((gift) => {
          var giftRawBase = parseInt(gift.dataset.rawBasePrice, 10);
          if (isNaN(giftRawBase)) return;
          var giftRawCompare = parseInt(gift.dataset.rawBaseComparePrice, 10);
          if (isNaN(giftRawCompare)) giftRawCompare = giftRawBase;
          var giftDiscountType = gift.dataset.discountType || 'none';
          var giftDiscountValue = parseFloat(gift.dataset.discountValue) || 0;
          var giftDiscounted = this.applyDiscount(giftRawBase, giftRawCompare, giftDiscountType, giftDiscountValue, rate);

          gift.dataset.giftPrice = giftDiscounted.price;

          var giftPriceEl = gift.querySelector('.upsell-gift__price');
          if (giftPriceEl) giftPriceEl.textContent = this.formatMoney(giftDiscounted.price);
          var giftCompareEl = gift.querySelector('.upsell-gift__compare-price');
          if (giftCompareEl) {
            giftCompareEl.textContent =
              giftDiscounted.compare > giftDiscounted.price ? this.formatMoney(giftDiscounted.compare) : '';
          }
        });

        this.updateTierPrice(tier);
      });
    }

    // Mirrors the percent/fixed discount math in upsell-tier-card.liquid and
    // upsell-addon-row.liquid so a variant swap on the client stays consistent
    // with what the server would have rendered for that variant.
    applyDiscount(basePrice, baseCompare, discountType, discountValue, rate) {
      if (discountType === 'percent' && discountValue > 0) {
        return { price: Math.round((basePrice * (100 - discountValue)) / 100), compare: basePrice };
      }
      if (discountType === 'fixed' && discountValue > 0) {
        // discountValue is entered in the shop's base currency — convert it
        // with the visitor's live exchange rate before subtracting, so it
        // stays a meaningful discount in every currency, not just the base one.
        var effectiveRate = rate || this.getCurrencyRate();
        var price = basePrice - Math.round(discountValue * 100 * effectiveRate);
        return { price: Math.max(price, 0), compare: basePrice };
      }
      return { price: basePrice, compare: baseCompare };
    }

    onTierVariantChange(tier, variants) {
      var picker = tier.querySelector('[data-tier-variants]');
      if (!picker) return;
      var values = this.getSelectedOptionValues(picker);
      var match = this.findMatchingVariant(variants, values);
      if (!match) return;

      var qty = parseInt(tier.dataset.quantity, 10) || 1;
      var rawBase = match.price * qty;
      var compareSource = match.compare_at_price > match.price ? match.compare_at_price : match.price;
      var rawCompare = compareSource * qty;
      var discountType = tier.dataset.discountType || 'none';
      var discountValue = parseFloat(tier.dataset.discountValue) || 0;
      var discounted = this.applyDiscount(rawBase, rawCompare, discountType, discountValue);

      tier.dataset.variantId = match.id;
      tier.dataset.basePrice = discounted.price;
      tier.dataset.baseComparePrice = discounted.compare;

      var compareEl = tier.querySelector('[data-tier-compare-price]');
      if (compareEl) {
        compareEl.textContent = discounted.compare > discounted.price ? this.formatMoney(discounted.compare) : '';
      }

      var photoEl = tier.querySelector('[data-tier-variant-photo]');
      if (photoEl && match.featured_image && match.featured_image.src) {
        var sep = match.featured_image.src.indexOf('?') === -1 ? '?' : '&';
        photoEl.src = match.featured_image.src + sep + 'width=80';
      }

      this.updateTierPrice(tier);
    }

    // Same idea as onTierVariantChange, but scoped to a single unit inside a
    // multi-quantity tier so each unit can carry its own variant (color) while
    // the tier's total price still comes from the shared base variant price —
    // per-unit color pickers here are for choosing WHICH variant, not for
    // handling per-variant price differences.
    onTierUnitVariantChange(tier, unitEl, variants) {
      var values = this.getSelectedOptionValues(unitEl);
      var match = this.findMatchingVariant(variants, values);
      if (!match) return;

      unitEl.dataset.variantId = match.id;

      var photoEl = unitEl.querySelector('[data-tier-variant-photo]');
      if (photoEl && match.featured_image && match.featured_image.src) {
        var sep = match.featured_image.src.indexOf('?') === -1 ? '?' : '&';
        photoEl.src = match.featured_image.src + sep + 'width=80';
      }

      // Keep tier.dataset.variantId pointing at unit 1's variant so any code
      // that still reads it (e.g. sticky ATC) has a sensible representative.
      if (unitEl.dataset.unitIndex === '1') {
        tier.dataset.variantId = match.id;
      }
    }

    onAddonVariantChange(addon, variants) {
      var picker = addon.querySelector('[data-addon-variants]');
      if (!picker) return;
      var values = this.getSelectedOptionValues(picker);
      var match = this.findMatchingVariant(variants, values);
      if (!match) return;

      var compareSource = match.compare_at_price > match.price ? match.compare_at_price : match.price;
      var discountType = addon.dataset.discountType || 'none';
      var discountValue = parseFloat(addon.dataset.discountValue) || 0;
      var discounted = this.applyDiscount(match.price, compareSource, discountType, discountValue);

      addon.dataset.variantId = match.id;
      addon.dataset.price = discounted.price;

      var priceEl = addon.querySelector('.upsell-addon__price');
      var compareEl = addon.querySelector('.upsell-addon__compare-price');
      if (priceEl) priceEl.textContent = this.formatMoney(discounted.price);
      if (compareEl) {
        compareEl.textContent = discounted.compare > discounted.price ? this.formatMoney(discounted.compare) : '';
      }

      var tier = addon.closest('[data-tier]');
      if (tier) this.updateTierPrice(tier);
    }

    connectToForm() {
      var formId = this.dataset.formId;
      if (!formId) return;
      var wrapper = document.getElementById(formId);
      if (!wrapper) return;
      this.form = wrapper.tagName === 'FORM' ? wrapper : wrapper.querySelector('form');
      if (!this.form) return;
      this.submitBtn = this.form.querySelector('button[type="submit"], .product-form__submit');
      if (this.submitBtn) {
        // Capture phase + preventDefault so the theme's own submit handler never
        // runs for this form: we take over the whole add-to-cart request ourselves
        // (main product + add-ons in one atomic call) to avoid the two competing
        // requests that used to sometimes fail or leave the drawer out of sync.
        this.submitBtn.addEventListener(
          'click',
          (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.addAllToCart();
          },
          true
        );
      }
    }

    // Prices baked into the page (data-price attributes) are always in the
    // shop's base currency. If Shopify Markets / multi-currency is active for
    // this visitor, window.Shopify.currency holds the active currency code and
    // the live conversion rate — apply it here so the on-page recalculation
    // (when add-ons are checked/unchecked) matches the visitor's own currency
    // instead of always showing the shop's base currency.
    formatMoney(cents) {
      // `cents` always comes from prices Liquid already rendered (baked into
      // data-* attributes) or from the variants JSON, both of which Shopify
      // has already localized to the visitor's currency when Markets is
      // active — so this only needs to format them, never convert them again.
      var currencyCode = this.currencyCode;
      if (!currencyCode && window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
        currencyCode = window.Shopify.currency.active;
      }
      var amount = cents / 100;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency',
          currency: currencyCode,
          currencyDisplay: this.showCurrencyCode ? 'code' : 'symbol',
        }).format(amount);
      } catch (e) {
        var formatted = this.moneyFormat.replace(/\{\{\s*amount\s*\}\}/, amount.toFixed(2));
        if (this.showCurrencyCode) formatted += ' ' + currencyCode;
        return formatted;
      }
    }

    selectTier(selected, syncForm) {
      this.tiers.forEach((tier) => {
        tier.dataset.selected = tier === selected ? 'true' : 'false';
      });
      if (syncForm) this.syncFormQuantity(selected);
    }

    syncFormQuantity(tier) {
      if (!this.form) return;
      var qty = tier.dataset.quantity;
      var existing = this.form.querySelectorAll('input[name="quantity"]');
      if (existing.length > 1) {
        var matched = null;
        existing.forEach((radio) => {
          if (radio.value === qty) matched = radio;
        });
        if (matched && !matched.checked) {
          matched.checked = true;
          matched.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else if (existing.length === 1) {
        existing[0].value = qty;
        existing[0].dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        var hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'quantity';
        hidden.value = qty;
        hidden.setAttribute('data-upsell-quantity', 'true');
        this.form.appendChild(hidden);
      }
    }

    updateTierPrice(tier) {
      var base = parseInt(tier.dataset.basePrice, 10) || 0;
      var addonsTotal = 0;
      tier.querySelectorAll('[data-addon]').forEach((addon) => {
        var checkbox = addon.querySelector('[data-addon-checkbox]');
        if (checkbox && checkbox.checked) {
          addonsTotal += parseInt(addon.dataset.price, 10) || 0;
        }
      });
      var total = base + addonsTotal;
      var priceEl = tier.querySelector('[data-tier-price]');
      if (priceEl) priceEl.textContent = this.formatMoney(total);
    }

    addAllToCart() {
      if (this.busy || !this.form) return;
      var selected = this.tiers.find((t) => t.dataset.selected === 'true') || this.tiers[0];
      if (!selected) return;

      var items = [];
      var unitEls = Array.from(selected.querySelectorAll('[data-tier-unit]'));
      if (unitEls.length) {
        // Multi-unit tier: one line item per unit so each can carry its own
        // variant (e.g. 1 Black + 1 White) instead of one line item at qty 2.
        unitEls.forEach((unitEl) => {
          var unitVariantId = unitEl.dataset.variantId;
          if (unitVariantId) items.push({ id: parseInt(unitVariantId, 10), quantity: 1 });
        });
      } else {
        var mainVariantId = selected.dataset.variantId;
        var mainQty = parseInt(selected.dataset.quantity, 10) || 1;
        if (mainVariantId) items.push({ id: parseInt(mainVariantId, 10), quantity: mainQty });
      }

      selected.querySelectorAll('[data-addon]').forEach((addon) => {
        var checkbox = addon.querySelector('[data-addon-checkbox]');
        var addonVariantId = addon.dataset.variantId;
        if (checkbox && checkbox.checked && addonVariantId) {
          items.push({ id: parseInt(addonVariantId, 10), quantity: 1 });
        }
      });

      // Free gifts have no checkbox — they're always included with the tier.
      selected.querySelectorAll('[data-gift]').forEach((gift) => {
        var giftVariantId = gift.dataset.variantId;
        if (giftVariantId) items.push({ id: parseInt(giftVariantId, 10), quantity: 1 });
      });

      if (!items.length) return;

      this.busy = true;
      if (this.submitBtn) this.submitBtn.setAttribute('aria-disabled', 'true');

      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var cartDrawer = document.querySelector('cart-drawer');
      var sections = cartDrawer && typeof cartDrawer.getSectionsToRender === 'function'
        ? cartDrawer.getSectionsToRender().map((s) => s.id)
        : ['cart-drawer', 'cart-icon-bubble'];

      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: items,
          sections: sections,
          sections_url: window.location.pathname,
        }),
      })
        .then((res) => res.json())
        .then((state) => {
          if (state.status) {
            // Shopify returns an error object ({status, message, description}) on failure.
            throw new Error(state.description || state.message || 'Cart error');
          }
          this.refreshCartUI(state);
        })
        .catch((err) => {
          console.error('Upsell bundles add to cart failed:', err);
          window.location.href = root + 'cart';
        })
        .finally(() => {
          this.busy = false;
          if (this.submitBtn) this.submitBtn.removeAttribute('aria-disabled');
        });
    }

    // Rebuilds the cart drawer and header cart icon directly from the
    // `sections` payload returned by /cart/add.js, instead of relying on the
    // theme's own cart-drawer.renderContents() (which sometimes silently
    // failed partway through and left the drawer showing stale/empty content
    // even though the items had really been added to the cart).
    refreshCartUI(state) {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var parser = new DOMParser();

      if (state.sections && state.sections['cart-drawer']) {
        var doc = parser.parseFromString(state.sections['cart-drawer'], 'text/html');
        var freshDrawer = doc.querySelector('cart-drawer');
        var currentDrawer = document.querySelector('cart-drawer');
        if (freshDrawer && currentDrawer) {
          currentDrawer.replaceWith(freshDrawer);
          if (typeof freshDrawer.open === 'function') {
            try {
              freshDrawer.open();
            } catch (e) {
              freshDrawer.classList.add('active');
              document.documentElement.classList.add('overflow-hidden');
            }
          } else {
            freshDrawer.classList.add('active');
            document.documentElement.classList.add('overflow-hidden');
          }
        }
      }

      if (state.sections && state.sections['cart-icon-bubble']) {
        var iconDoc = parser.parseFromString(state.sections['cart-icon-bubble'], 'text/html');
        var freshIcon = iconDoc.body;
        var iconTarget = document.getElementById('cart-icon-bubble');
        if (freshIcon && iconTarget) iconTarget.innerHTML = freshIcon.innerHTML;
      }

      if (!state.sections) {
        window.location.href = root + 'cart';
      }
    }
  }

  customElements.define('upsell-bundles', UpsellBundles);
})();
